import React, {
  createContext,
  useReducer,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import {
  fetchActiveBanners,
  fetchStandings as fetchLeagueStandings,
  fetchAllPlayerTransactions,
  FeedItem,
  Feed,
  ShopProduct,
  fetchLeaderByStatList,
  fetchSeasonEvents,
} from '../../Api'
import {
  STATS_YEAR,
  EVENTS_YEAR,
  hasChampSeriesStarted as showChampSeries,
  STANDINGS_YEAR,
} from '../../Config/League/league.config'
import { champSeriesStatTitles, statTitles } from '../../Constant/player'
import { filterStandings, mapPlayerStat, SEASON_SEGMENT_NAMES } from '../../Utils'
import type { LeaderResult } from './types'
import type {
  Banner,
  LeaderData,
  TeamStanding,
  EventGQL,
  PlayerTransactions,
} from '../../Api'
import {
  checkHasChampSeriesStarted,
  getCurrentWeek,
} from '../../Utils/Events/eventHelpers'
import { getFeedByTag, getHomeFeed } from '../../Api/Feed/feed.api'
import { SeasonContext } from '../SeasonContext'
import { fetchShopCollection } from '../../Api/Shop/shop.api'
import { SHOP_COLLECTIONS } from '../../Config/Shop/shop.config'
import { ShopProductSortKeys } from '../../Api/Shop/shop.utils'
import { StorageContext } from '../StorageContext'
import { STORAGE_NAMES } from '../../Constant'
import { SEASON_SELECTOR_TYPES } from '../../Constant/season'
import { leagueReducer, initialState, LEAGUE_ACTIONS } from './reducer'
import { HomeFeed } from '../../Api/Feed/feed.types'

type LeagueContextType = {
  events: EventGQL[]
  eventsLoading: boolean
  currentWeek: null | string
  standings: TeamStanding[]
  feed: Feed | null
  homeFeed: HomeFeed | null
  teamFeed: FeedItem[]
  isFetchingMainFeed: boolean
  isFetchingTeamFeed: boolean
  feedLoading: boolean
  leaders: LeaderResult[]
  banners: Banner[]
  bestSellingProducts: ShopProduct[]
  playerTransactions: PlayerTransactions[]
  fetchTeamFeed: (teamId: string) => Promise<FeedItem[] | null>
  fetchStandings: () => Promise<void>
  fetchLeaders: () => Promise<LeaderResult[]>
  fetchPlayerTransaction: () => PlayerTransactions[] | Promise<PlayerTransactions[]>
  getTeamEvents: (team: string) => EventGQL[]
  updateEventInState: (event: EventGQL) => void
  fetchEvents: () => Promise<void>
}

export const LeagueContext = createContext<LeagueContextType | null>(null)

export const LeagueProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { seasonSelect } = useContext(SeasonContext)!
  const standingsSelectorType = SEASON_SELECTOR_TYPES.STANDINGS
  const { isStorageReady, store, fetchAndCache } = useContext(StorageContext)!
  const [state, dispatch] = useReducer(leagueReducer, initialState)
  const mountedRef = useRef(true)

  const fetchData = useCallback(async (): Promise<void> => {
    await Promise.all([
      fetchBanners(),
      fetchEvents(),
      fetchStandings(),
      fetchHomeFeed(),
      fetchMainShopCollection(),
    ])
  }, [])

  useEffect(() => {
    mountedRef.current = true
    if (isStorageReady) {
      fetchData()
    }
    return () => {
      mountedRef.current = false
    }
  }, [isStorageReady, fetchData])

  const fetchEvents = useCallback(async (): Promise<void> => {
    dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS_LOADING, payload: true })

    const fetcher = async (): Promise<EventGQL[]> => {
      try {
        const result = await fetchSeasonEvents(EVENTS_YEAR, showChampSeries, true)
        return result || []
      } catch (err) {
        console.error('fetchEvents error', err)
        return []
      }
    }

    const onUpdate = (events: EventGQL[]) => {
      store(STORAGE_NAMES.seasonEvents, events)
      if (!mountedRef.current) return
      if (events.length > 0) {
        dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS, payload: events })
        dispatch({
          type: LEAGUE_ACTIONS.SET_CURRENT_WEEK,
          payload: getCurrentWeek(events),
        })
        dispatch({
          type: LEAGUE_ACTIONS.SET_HAS_CHAMP_SERIES_STARTED,
          payload: checkHasChampSeriesStarted(events),
        })
      }
    }

    await fetchAndCache<EventGQL[]>(STORAGE_NAMES.seasonEvents, fetcher, onUpdate)
    dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS_LOADING, payload: false })
  }, [fetchAndCache])

  const fetchBanners = useCallback(async (): Promise<void> => {
    const fetcher = async (): Promise<Banner[]> => {
      try {
        const banners = await fetchActiveBanners(true)
        return banners ?? []
      } catch (err) {
        console.error('fetchBanners error', err)
        return []
      }
    }

    const onUpdate = (banners: Banner[]) => {
      store(STORAGE_NAMES.banners, banners)
      if (!mountedRef.current) return
      dispatch({ type: LEAGUE_ACTIONS.SET_BANNERS, payload: banners })
    }

    await fetchAndCache<Banner[]>(STORAGE_NAMES.banners, fetcher, onUpdate)
  }, [fetchAndCache, store])

  const fetchMainShopCollection = useCallback(async (): Promise<void> => {
    const fetcher = async (): Promise<ShopProduct[]> => {
      try {
        const collection = await fetchShopCollection(
          SHOP_COLLECTIONS.MAIN,
          ShopProductSortKeys.BEST_SELLING,
          10
        )
        return collection?.products || []
      } catch (err) {
        console.error('fetchMainShopCollection error', err)
        return []
      }
    }

    const onUpdate = (products: ShopProduct[]) => {
      store(STORAGE_NAMES.home_shop_collection, products)
      if (!mountedRef.current) return
      dispatch({
        type: LEAGUE_ACTIONS.SET_BEST_SELLING_PRODUCTS,
        payload: products,
      })
    }

    await fetchAndCache<ShopProduct[]>(
      STORAGE_NAMES.home_shop_collection,
      fetcher,
      onUpdate
    )
  }, [fetchAndCache, store])

  const fetchHomeFeed = useCallback(async (): Promise<void> => {
    dispatch({ type: LEAGUE_ACTIONS.SET_FEED_LOADING, payload: true })
    dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_MAIN_FEED, payload: true })

    const fetcher = async (): Promise<HomeFeed> => {
      try {
        const feedRes = await getHomeFeed({})
        if (!feedRes) {
          return {
            trending: null,
            latest: null,
            headlines: null,
            S3: null,
            S4: null,
            team: null,
          }
        }
        const trending =
          feedRes.home.find((section) => section.location === 'S1') || null
        const headlines =
          feedRes.home.find((section) => section.tags.includes('headlines')) || null
        const latest =
          feedRes.home.find((section) => section.tags.includes('latest')) || null
        const S3 = feedRes.home.find((section) => section.location === 'S3') || null
        const S4 = feedRes.home.find((section) => section.location === 'S4') || null

        const feed = {
          trending,
          latest,
          headlines,
          S3,
          S4,
          team: feedRes.team?.[0] || null,
        }
        return feed
      } catch (err) {
        console.error('fetchHomeFeed error', err)
        return {
          trending: null,
          latest: null,
          headlines: null,
          S3: null,
          S4: null,
          team: null,
        }
      }
    }

    const onUpdate = (feed: HomeFeed) => {
      store(STORAGE_NAMES.home_feed, feed)
      if (!mountedRef.current) return
      dispatch({ type: LEAGUE_ACTIONS.SET_HOME_FEED, payload: feed })
      dispatch({ type: LEAGUE_ACTIONS.SET_FEED_LOADING, payload: false })
      dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_MAIN_FEED, payload: false })
    }
    try {
      await fetchAndCache<HomeFeed>(STORAGE_NAMES.home_feed, fetcher, onUpdate)
    } finally {
      dispatch({ type: LEAGUE_ACTIONS.SET_FEED_LOADING, payload: false })
      dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_MAIN_FEED, payload: false })
    }
  }, [fetchAndCache, store])

  const fetchTeamFeed = useCallback(
    async (teamId: string): Promise<FeedItem[] | null> => {
      if (state.isFetchingTeamFeed) return null
      dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_TEAM_FEED, payload: true })
      try {
        const teamFeed = await getFeedByTag({ tags: [teamId] })
        if (!teamFeed?.length) return null
        if (mountedRef.current) {
          dispatch({ type: LEAGUE_ACTIONS.SET_TEAM_FEED, payload: teamFeed })
        }
        return teamFeed
      } catch (err) {
        console.error('fetchTeamFeed error', err)
        return null
      } finally {
        dispatch({ type: LEAGUE_ACTIONS.SET_IS_FETCHING_TEAM_FEED, payload: false })
      }
    },
    []
  )

  const fetchStandings = useCallback(async (): Promise<void> => {
    const isCS =
      seasonSelect[standingsSelectorType].seasonType === SEASON_SEGMENT_NAMES.champseries

    const fetcher = async (): Promise<TeamStanding[]> => {
      try {
        const leagueStandings = await fetchLeagueStandings(STANDINGS_YEAR, isCS)
        return leagueStandings || []
      } catch (err) {
        console.error('fetchStandings error', err)
        return []
      }
    }

    const onUpdate = (standings: TeamStanding[]) => {
      store(`${STORAGE_NAMES.standings}_${seasonSelect.year}`, standings)
      if (!mountedRef.current) return
      const teams = filterStandings(standings, seasonSelect[standingsSelectorType])
      dispatch({ type: LEAGUE_ACTIONS.SET_STANDINGS, payload: teams })
    }

    await fetchAndCache<TeamStanding[]>(
      `${STORAGE_NAMES.standings}_${seasonSelect.year}`,
      fetcher,
      onUpdate
    )
  }, [fetchAndCache, seasonSelect, standingsSelectorType, store])

  const fetchLeaders = useCallback(async (): Promise<LeaderResult[]> => {
    if (state.leaders.length > 0) return state.leaders
    const titlesToUse = state.hasChampSeriesStarted ? champSeriesStatTitles : statTitles
    const seasonSegment = state.hasChampSeriesStarted
      ? SEASON_SEGMENT_NAMES.champseries
      : SEASON_SEGMENT_NAMES.regular
    try {
      const data = await fetchLeaderByStatList(STATS_YEAR, 1, seasonSegment, titlesToUse)
      if (!data || data.length < 1) return []
      const filtered = data.reduce(function (result: LeaderData[], ldr) {
        if (ldr) {
          result.push(ldr)
          return result
        }
        return result
      }, [])
      const leaders = filtered.map((ldr) => ({
        ...ldr,
        statName: mapPlayerStat(ldr.statType),
      }))
      dispatch({ type: LEAGUE_ACTIONS.SET_LEADERS, payload: leaders })
      return leaders
    } catch (err) {
      console.error('Error getting leaders', err)
      return []
    }
  }, [])

  const fetchPlayerTransaction = useCallback(async (): Promise<PlayerTransactions[]> => {
    if (state.playerTransactions.length > 0) {
      return state.playerTransactions
    }
    try {
      const pTransactions = await fetchAllPlayerTransactions()
      const transactions = pTransactions?.playerTransactions || []
      dispatch({ type: LEAGUE_ACTIONS.SET_PLAYER_TRANSACTIONS, payload: transactions })
      return transactions
    } catch (err) {
      console.error('fetchPlayerTransaction error', err)
      return []
    }
  }, [])

  const getTeamEvents = useCallback((team: string): EventGQL[] => {
    return state.events.filter(
      (event) =>
        event.homeTeam?.officialId === team || event.awayTeam?.officialId === team
    )
  }, [])

  const updateEventInState = useCallback(
    (event: EventGQL) => {
      const updatedEvents = state.events.map((e) => (e.id === event.id ? event : e))
      dispatch({ type: LEAGUE_ACTIONS.SET_EVENTS, payload: updatedEvents })
      store(STORAGE_NAMES.seasonEvents, updatedEvents)
    },
    [store]
  )

  const contextValue = useMemo(
    () => ({
      events: state.events,
      eventsLoading: state.eventsLoading,
      currentWeek: state.currentWeek,
      standings: state.standings,
      feed: state.feed,
      homeFeed: state.homeFeed,
      teamFeed: state.teamFeed,
      isFetchingMainFeed: state.isFetchingMainFeed,
      isFetchingTeamFeed: state.isFetchingTeamFeed,
      feedLoading: state.feedLoading,
      leaders: state.leaders,
      banners: state.banners,
      bestSellingProducts: state.bestSellingProducts,
      playerTransactions: state.playerTransactions,
      fetchEvents,
      fetchTeamFeed,
      fetchStandings,
      fetchLeaders,
      fetchPlayerTransaction,
      getTeamEvents,
      updateEventInState,
    }),
    [
      state.events,
      state.eventsLoading,
      state.currentWeek,
      state.standings,
      state.feed,
      state.homeFeed,
      state.teamFeed,
      state.isFetchingMainFeed,
      state.isFetchingTeamFeed,
      state.feedLoading,
      state.leaders,
      state.banners,
      state.bestSellingProducts,
      state.playerTransactions,
      fetchEvents,
      fetchTeamFeed,
      fetchStandings,
      fetchLeaders,
      fetchPlayerTransaction,
      getTeamEvents,
      updateEventInState,
    ]
  )

  return <LeagueContext.Provider value={contextValue}>{children}</LeagueContext.Provider>
}
